import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { isEffectsDisabledState } from '@iheartjane/menu';
import type { Nullable, PriceId, ProductKind } from '@iheartjane/types';
import flatten from 'lodash-es/flatten';
import isEmpty from 'lodash-es/isEmpty';
import omit from 'lodash-es/omit';
import reduce from 'lodash-es/reduce';
import sortBy from 'lodash-es/sortBy';
import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router';

import type { OptionalMenuRouteParams } from '@jane/kiosk/types';
import type {
  AlgoliaFacet,
  AlgoliaFacets,
  FilterGroup,
  FilterKeys,
  FilterSelectItem,
  RangeFilterGroup,
  SelectFilterGroup,
} from '@jane/search/types';
import {
  CATEGORY_COLORS,
  CATEGORY_ICONS,
  FEELING_ICONS,
  JANE_GOLD_ICON,
  LINEAGE_COLORS,
  LINEAGE_ICONS,
  WEIGHT_LABELS,
  facetToFilter,
  getPriceBucketCount,
  getRatingBucketCount,
  getWeightSortOrder,
  overrideCustomCategoryLabels,
  sortAndFilterCategories,
} from '@jane/search/util';
import type { Store, StoreSpecial } from '@jane/shared/types';

import {
  capitalize,
  getSubcategories,
  getValidFeelings,
  hasFilterableOptions,
} from '../../../../util';
import { useComplianceLanguage } from '../../ComplianceLanguageProvider';
import {
  buildFilterRangeItems,
  hasFacetCounts,
  isFilterGroup,
  validateRatingFilterGroup,
} from './utils';

interface PresentationOptions {
  canFilterByCategory?: boolean;
  canFilterByJaneGold?: boolean;
  canFilterByRating?: boolean;
  canFilterBySpecial?: boolean;
  canFilterBySubcategoriesOf?: Nullable<ProductKind>;
  visiblePriceIds?: PriceId[];
}

const specialFilterSelections = (
  applicableSpecials: Record<string, number>,
  storeSpecials: (StoreSpecial | V3StoreSpecial)[]
): FilterSelectItem[] =>
  Object.entries(applicableSpecials).reduce(
    (acc: FilterSelectItem[], applicableSpecial) => {
      const matchingSpecial = storeSpecials.find(
        ({ id }) => id.toString() === applicableSpecial[0]
      );
      if (!matchingSpecial) {
        return acc;
      } else {
        return [
          ...acc,
          {
            count: applicableSpecial[1],
            label: matchingSpecial.title,
            value: applicableSpecial[0],
          },
        ];
      }
    },
    []
  );

const potencyPercentages = (percentages: AlgoliaFacet = {}) => {
  return Object.entries(percentages)
    .filter(([_percent, productCount]) => productCount > 0)
    .map(([percent]) => parseFloat(percent))
    .flat();
};

export const useFormattedFilterGroups = (
  {
    aggregate_rating: aggregateRating,
    available_weights: availableWeights,
    applicable_special_ids,
    brand,
    bucket_price: bucketPrice,
    category,
    percent_cbd: percentCbd,
    percent_thc: percentThc,
    root_types,
    feelings,
    has_brand_discount: hasBrandDiscount,
    compound_names: terpenes,
  }: AlgoliaFacets,
  store: Store,
  {
    canFilterByCategory = true,
    canFilterByJaneGold = false,
    canFilterByRating = true,
    canFilterBySubcategoriesOf = null,
    canFilterBySpecial = true,
    visiblePriceIds = [],
  }: PresentationOptions,
  specials: (StoreSpecial | V3StoreSpecial)[]
): FilterGroup[] => {
  const hasTerpeneFiltersAccess = useFlag(FLAGS.scTerpeneFiltersMarketplace);

  const { specialId } = useParams<OptionalMenuRouteParams>();
  const { pathname } = useLocation();
  const { getWord } = useComplianceLanguage();
  const specialFilterItems = specialFilterSelections(
    applicable_special_ids ?? {},
    specials
  );

  return useMemo(() => {
    const thcPercentages = potencyPercentages(percentThc);
    const cbdPercentages = potencyPercentages(percentCbd);

    const subcategoryFacets = getSubcategories(
      { root_types },
      store,
      canFilterBySubcategoriesOf
    );
    const allSubCategories = sortBy(
      flatten(Object.values(subcategoryFacets)),
      'label'
    );

    const validFeelings = getValidFeelings(store, feelings);

    const customCategoryLabels = overrideCustomCategoryLabels(
      store?.state === 'California',
      store?.custom_product_type_labels
    );

    const goldCounts = omit(hasBrandDiscount, 'false');

    const currentSpecial = specials.find(({ id }) => `${id}` === specialId);
    const spendXGetYPage =
      currentSpecial?.special_type === 'spending_threshold';

    const brandSpecialPage = pathname.includes('/jane-gold');
    const renderJaneGoldFilter =
      canFilterByJaneGold &&
      !brandSpecialPage &&
      !spendXGetYPage && //TODO remove when kiosk gold facets are moved off of algolia
      !isEmpty(goldCounts);

    const renderSaleFilter =
      root_types?.['sale'] && store?.custom_rows?.includes('sale');

    const dealsAndRewardsFilters: Nullable<SelectFilterGroup> =
      renderJaneGoldFilter || renderSaleFilter
        ? {
            key: 'deals_and_rewards' as FilterKeys, // casting since this is a custom key that is not actually used for filtering
            label: 'Deals and rewards',
            showInFilterBar: false,
            modalItemsPerRow: 2,
            type: 'multiselect',
            items: [
              ...(renderJaneGoldFilter
                ? facetToFilter(goldCounts, {
                    filterGroup: 'Jane Gold',
                    customLabels: { true: 'Jane Gold' },
                    icons: JANE_GOLD_ICON,
                    key: 'has_brand_discount',
                  })
                : []),
              ...(renderSaleFilter
                ? facetToFilter(
                    {
                      sale: root_types['sale'],
                    },
                    {
                      customLabels: customCategoryLabels,
                      filterGroup: 'Sale',
                      icons: CATEGORY_ICONS,
                      iconColor: CATEGORY_COLORS,
                      key: 'root_types',
                      storeId: store?.id,
                    },
                    store?.state
                  )
                : []),
            ],
          }
        : null;

    const categoryFilter: Nullable<SelectFilterGroup> =
      canFilterByCategory && root_types
        ? {
            items: sortAndFilterCategories(
              facetToFilter(
                root_types,
                {
                  customLabels: customCategoryLabels,
                  filterGroup: 'Category',
                  iconColor: CATEGORY_COLORS,
                  icons: CATEGORY_ICONS,
                  storeId: store?.id,
                },
                store?.state
              ).filter((item) => {
                // pull out sale from categories to be rendered in 'Deals and rewards' section
                if (item.value === 'sale') {
                  return false;
                }
                return item;
              }),
              store?.custom_product_type_ranking,
              store?.custom_rows,
              store?.menu_tabs
            ),
            key: 'root_types',
            label: 'Category',
            showInFilterBar: true,
            type: 'multiselect',
          }
        : null;

    const subcategories: Nullable<SelectFilterGroup> =
      subcategoryFacets && canFilterBySubcategoriesOf
        ? {
            items: facetToFilter(
              reduce<FilterSelectItem, { [label: string]: number }>(
                allSubCategories,
                (acc, subCat) => {
                  return { ...acc, [subCat.value]: subCat.count };
                },
                {}
              ),
              {
                customLabels: customCategoryLabels,
                filterGroup: 'Subcategory',
              }
            ),
            key: 'root_types',
            label: 'Subcategory',
            showInFilterBar: true,
            type: 'multiselect',
          }
        : null;

    const brandFilter: Nullable<SelectFilterGroup> = brand
      ? {
          items: facetToFilter(brand, { filterGroup: 'Brands' }),
          key: 'brand',
          label: 'Brand',
          showInFilterBar: true,
          type: 'multiselect',
          typeahead: true,
        }
      : null;

    const lineageFilter: Nullable<SelectFilterGroup> = category
      ? {
          items: facetToFilter(category, {
            customLabels: store.custom_lineage_labels,
            filterGroup: 'Lineage',
            iconColor: LINEAGE_COLORS,
            icons: LINEAGE_ICONS,
            storeId: store.id,
          }),
          key: 'category',
          label: 'Lineage',
          showInFilterBar: false,
          type: 'multiselect',
        }
      : null;

    const specialFilter: Nullable<SelectFilterGroup> =
      canFilterBySpecial && hasFilterableOptions(specialFilterItems)
        ? {
            items: specialFilterItems,
            key: 'applicable_special_ids',
            label: capitalize(getWord('deal') ?? 'Deal'),
            modalItemsPerRow: 2,
            showInFilterBar: false,
            type: 'multiselect',
          }
        : null;

    const feelingsFilter: Nullable<SelectFilterGroup> =
      !isEffectsDisabledState(store.state) && validFeelings
        ? {
            items: facetToFilter(validFeelings, {
              filterGroup: 'Feeling',
              icons: FEELING_ICONS,
            }),
            key: 'feelings',
            label: 'Feeling',
            showInFilterBar: false,
            type: 'multiselect',
          }
        : null;

    const ratingBucketCount = getRatingBucketCount(aggregateRating || {});
    const isRatingFilterGroupValid =
      validateRatingFilterGroup(ratingBucketCount);

    const ratingFilter: Nullable<SelectFilterGroup> =
      canFilterByRating && isRatingFilterGroupValid
        ? {
            items: ratingBucketCount,
            key: 'aggregate_rating',
            label: 'Rating',
            modalItemsPerRow: 4,
            showInFilterBar: false,
            type: 'multiselect',
          }
        : null;

    const terpeneFilters: Nullable<SelectFilterGroup> =
      hasTerpeneFiltersAccess && terpenes && hasFacetCounts(terpenes)
        ? {
            items: facetToFilter(terpenes, {
              filterGroup: 'Terpenes',
            }),
            key: 'compound_names',
            label: 'Top terpenes',
            showInFilterBar: false,
            type: 'multiselect',
            typeahead: true,
          }
        : null;

    const potencyFilterRangeItems = buildFilterRangeItems({
      cbdPercentages,
      thcPercentages,
    });

    const thcCbdRangeFilter: Nullable<RangeFilterGroup> =
      potencyFilterRangeItems
        ? {
            items: potencyFilterRangeItems,
            key: 'percent_thc',
            label: '',
            nested: true,
            showInFilterBar: false,
            type: 'range',
          }
        : null;

    const bucketPriceFilter: Nullable<SelectFilterGroup> = bucketPrice
      ? {
          items: getPriceBucketCount(bucketPrice),
          key: 'bucket_price',
          label: 'Price',
          showInFilterBar: false,
          type: 'multiselect',
        }
      : null;

    const productWeights = availableWeights
      ? Object.keys(WEIGHT_LABELS)
          .sort((a, b) => getWeightSortOrder(a) - getWeightSortOrder(b))
          .reduce((ordered: { [label: string]: number }, key) => {
            const formattedPriceIds =
              visiblePriceIds?.length > 0
                ? visiblePriceIds.map((id) => id.replace('_', ' '))
                : [];
            // Only include weights that are in visiblePriceIds (if specified)
            if (
              formattedPriceIds.length > 0 &&
              !formattedPriceIds.includes(key)
            ) {
              return ordered;
            }
            ordered[key] = availableWeights[key] || 0;
            return ordered;
          }, {})
      : null;

    const weightFilter: Nullable<SelectFilterGroup> = productWeights && {
      items: facetToFilter(productWeights, {
        filterGroup: 'Weight',
        storeId: store.id,
      }),
      key: 'available_weights',
      label: 'Weight',
      showInFilterBar: false,
      type: 'multiselect',
    };

    return [
      dealsAndRewardsFilters,
      categoryFilter,
      subcategories,
      brandFilter,
      lineageFilter,
      specialFilter,
      feelingsFilter,
      ratingFilter,
      thcCbdRangeFilter,
      terpeneFilters,
      bucketPriceFilter,
      weightFilter,
    ].filter<FilterGroup>(isFilterGroup);
  }, [
    aggregateRating,
    availableWeights,
    brand,
    bucketPrice,
    canFilterByCategory,
    canFilterByJaneGold,
    canFilterBySpecial,
    canFilterBySubcategoriesOf,
    category,
    feelings,
    getWord,
    hasBrandDiscount,
    hasTerpeneFiltersAccess,
    pathname,
    percentCbd,
    percentThc,
    root_types,
    specialFilterItems,
    store,
    terpenes,
  ]);
};
